import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import StatusBadge from "../../components/StatusBadge";
import { PageLoading, ErrorState } from "../../components/States";
import { fmtDateTime, fmtDateTimeZone, fmtMoney } from "../../lib/format";
import { useAdminData, act, SearchBox, FilterSelect, Pager, TableCard, TH } from "./shared";

const STATUSES = [["", "All statuses"], ["pending", "Pending"], ["pending_auth", "Pending auth"], ["authorized", "Authorized"], ["paid", "Paid"], ["completed", "Completed"], ["cancelled", "Cancelled"]];

export default function AdminBookings() {
  const [sp] = useSearchParams();
  const { data, error, page, setPage, filters, setFilters, reload } = useAdminData(api.adminBookings, { status: sp.get("status") || "" });
  const [q, setQ] = useState("");
  const rows = data?.items || [];

  return (
    <div data-testid="admin-bookings-page">
      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <SearchBox value={q} onChange={setQ} onSearch={() => setFilters((f) => ({ ...f, q }))} placeholder="Search booking id or listing…" testid="admin-bookings-search" />
        <FilterSelect value={filters.status || ""} onChange={(v) => setFilters((f) => ({ ...f, status: v }))} label="Status filter" testid="admin-bookings-status" options={STATUSES} />
        <input type="date" value={filters.date_from || ""} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
               aria-label="Created from" data-testid="admin-bookings-from" className="input-dark !w-auto !h-10 !py-0 text-xs" />
        <input type="date" value={filters.date_to || ""} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
               aria-label="Created to" data-testid="admin-bookings-to" className="input-dark !w-auto !h-10 !py-0 text-xs" />
      </div>
      {error ? <ErrorState body={error} onRetry={reload} /> : !data ? <PageLoading label="Loading bookings…" /> : (
        <>
          <TableCard testid="admin-bookings-table" empty={rows.length === 0 && "No bookings found."}
            head={<><TH>Booking</TH><TH>When</TH><TH>Vehicle</TH><TH>Total</TH><TH>Status</TH><TH>Session</TH><TH right>Actions</TH></>}>
            {rows.map((b) => (
              <tr key={b.booking_id} className="border-b border-[#18181B]" data-testid={`admin-booking-${b.booking_id}`}>
                <td className="p-4"><p className="font-semibold">{b.spot_title}</p><p className="text-xs text-zinc-500">{b.booking_id}</p></td>
                <td className="p-4 text-xs text-zinc-400">{fmtDateTimeZone(b.start_time, b.timezone || b.listing_timezone)}</td>
                <td className="p-4 text-xs text-zinc-400">{b.vehicle_snapshot ? "on file" : "—"}</td>
                <td className="p-4">{fmtMoney(b.total, (b.currency || "").toUpperCase() || undefined)}</td>
                <td className="p-4"><StatusBadge value={b.status} /></td>
                <td className="p-4 text-xs text-zinc-500">{b.session_completed_at ? "completed" : b.session_started_at ? "active" : "—"}{b.extension_count ? ` · +${b.extension_count} ext` : ""}</td>
                <td className="p-4 text-right space-x-2 whitespace-nowrap">
                  {["paid", "authorized"].includes(b.status) && (
                    <button data-testid={`booking-refund-${b.booking_id}`} onClick={() => { const r = window.prompt("Refund reason:", "Support review"); if (r !== null) act(() => api.adminRefund(b.booking_id, r), "Refunded", reload); }}
                            className="text-xs font-semibold px-3 py-1.5 rounded-full border border-[#27272A] hover:border-red-500 text-red-400 transition-colors">Refund</button>
                  )}
                  {b.status === "authorized" && (
                    <button data-testid={`booking-force-capture-${b.booking_id}`} onClick={() => window.confirm("Force-capture without OTP?") && act(() => api.adminOverrideOtp(b.booking_id), "Captured", reload)}
                            className="text-xs font-semibold px-3 py-1.5 rounded-full border border-[#27272A] hover:border-emerald-500 text-emerald-400 transition-colors">Force capture</button>
                  )}
                </td>
              </tr>
            ))}
          </TableCard>
          <Pager page={page} total={data.total} pageSize={data.page_size} onPage={setPage} testid="admin-bookings-pager" />
        </>
      )}
    </div>
  );
}
